/**
 * Shared Zod building blocks for form validation.
 *
 * Each form's rules live in a `z.object` whose keys are the form's error keys, in the order
 * the fields appear on screen. Zod reports issues in shape order, so the first error is the
 * first invalid field, which is the one the form moves focus to.
 */
import { z } from 'zod';

/** Shape-only email check shared by every form that takes an address. */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A string that must have something other than whitespace in it. */
export const requiredText = (message: string) =>
  z.string({ error: message }).trim().min(1, message);

/** A yes/no question that must have been answered: `null` (unanswered) fails. */
export const answered = (message: string) => z.boolean({ error: message });

/** A required email address, checked after trimming. */
export const requiredEmail = (requiredMessage: string, invalidMessage: string) =>
  requiredText(requiredMessage).regex(EMAIL_PATTERN, invalidMessage);

/** A typed "I acknowledge", in any case and with any surrounding space. */
export const acknowledged = (message: string) =>
  z.string({ error: message }).refine((value) => value.trim().toLowerCase() === 'i acknowledge', {
    error: message,
  });

/**
 * The first message for each top-level field, in the order Zod reported them. A field
 * that breaks several rules shows the first one, as a hand-written if/else chain would.
 */
export function firstErrorPerField(error: z.ZodError): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const issue of error.issues) {
    const field = String(issue.path[0] ?? '');
    if (!(field in errors)) {
      errors[field] = issue.message;
    }
  }
  return errors;
}

/** The first message Zod reported, for single-value schemas. */
export function firstError(error: z.ZodError): string {
  return error.issues[0]?.message ?? '';
}